import React, { useState, useEffect, useRef } from 'react';
import { Form, ListGroup } from 'react-bootstrap';

const AutoComplete = ({ suggestions = [], onSelect, preselected = null }) => {
  const [inputValue, setInputValue] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);

  useEffect(() => {
    // Add click outside listener
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update input value when preselected changes
  useEffect(() => {
    if (preselected) {
      setInputValue(`${preselected.lastName}, ${preselected.firstName} (${preselected.employeeNumber})`);
    }
  }, [preselected]);

  const filterSuggestions = (value) => {
    const inputValueLower = value.toLowerCase();
    return suggestions.filter(
      suggestion =>
        suggestion.firstName.toLowerCase().includes(inputValueLower) ||
        suggestion.lastName.toLowerCase().includes(inputValueLower) ||
        suggestion.employeeNumber.toLowerCase().includes(inputValueLower)
    );
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setFilteredSuggestions(filterSuggestions(value));
    setShowSuggestions(true);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'Enter':
        if (highlightedIndex >= 0) {
          handleSelect(filteredSuggestions[highlightedIndex]);
          e.preventDefault();
        }
        break;
      case 'ArrowUp':
        setHighlightedIndex(prev => 
          prev <= 0 ? filteredSuggestions.length - 1 : prev - 1
        );
        e.preventDefault();
        break;
      case 'ArrowDown':
        setHighlightedIndex(prev => 
          prev === filteredSuggestions.length - 1 ? 0 : prev + 1
        );
        e.preventDefault();
        break;
      case 'Escape':
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
      case 'Tab':
        if (filteredSuggestions.length > 0) {
          const selectedIndex = highlightedIndex >= 0 ? highlightedIndex : 0;
          handleSelect(filteredSuggestions[selectedIndex]);
        }
        break;
      default:
        break;
    }
  };

  const handleSelect = (suggestion) => {
    setInputValue(`${suggestion.lastName}, ${suggestion.firstName} (${suggestion.employeeNumber})`);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    onSelect(suggestion);
  };

  return (
    <>
      <Form.Control
        type="text"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (inputValue) {
            setShowSuggestions(true);
            setFilteredSuggestions(filterSuggestions(inputValue));
          }
        }}
        placeholder="Search for an employee..."
      />
      {showSuggestions && filteredSuggestions.length > 0 && (
        <ListGroup 
          className="position-absolute w-100 mt-1"
          style={{ 
            zIndex: 1000, 
            maxHeight: '200px', 
            overflowY: 'auto',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          {filteredSuggestions.map((suggestion, index) => (
            <ListGroup.Item
              key={suggestion.employeeId}
              action
              active={index === highlightedIndex}
              onClick={() => handleSelect(suggestion)}
              className="cursor-pointer"
              style={{ cursor: 'pointer' }}
            >
              {`${suggestion.lastName}, ${suggestion.firstName} (${suggestion.employeeNumber})`}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </>
  );
};

export default AutoComplete;